import { ReactNode } from 'react';

import {
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiMore2Fill,
  RiPencilLine,
} from '@remixicon/react';

import { ButtonIcon, Menu, MenuItem, MenuTrigger, Text } from '@backstage/ui';

import { DateUtils } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  ArtifactIcon,
  ArtifactListItem,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';

interface ExternalUrlArtifactListItemProps {
  artifact: AttachmentFragment;
  icon: ReactNode;
  subtitleLabel: string;
  openLabel: string;
  fallbackTitle?: (artifact: AttachmentFragment) => string;
  onDelete?: () => void;
  onRename?: () => void;
}

const defaultFallbackTitle = (artifact: AttachmentFragment) => artifact.uri;

export const ExternalUrlArtifactListItem = ({
  artifact,
  icon,
  subtitleLabel,
  openLabel,
  fallbackTitle = defaultFallbackTitle,
  onDelete,
  onRename,
}: ExternalUrlArtifactListItemProps) => {
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    DateUtils.MONTH_DAY_FORMAT,
  );

  const safe = isSafeUrl(artifact.uri);

  const open = () => {
    if (safe) {
      window.open(artifact.uri, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ArtifactListItem.Root onClick={safe ? open : undefined}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body-medium" as="div" truncate>
            {artifact.displayName || fallbackTitle(artifact)}
          </Text>
        }
        secondary={
          <Text variant="body-x-small" as="div" color="secondary" truncate>
            {subtitleLabel} &middot; {dateLabel}
          </Text>
        }
      />
      {(safe || onRename || onDelete) && (
        <ArtifactListItem.Action>
          <MenuTrigger>
            <ButtonIcon
              variant="tertiary"
              size="small"
              icon={<RiMore2Fill size={16} />}
              aria-label="Artifact actions"
            />
            <Menu placement="bottom end">
              {safe && (
                <MenuItem iconStart={<RiExternalLinkLine />} onAction={open}>
                  {openLabel}
                </MenuItem>
              )}
              {onRename && (
                <MenuItem iconStart={<RiPencilLine />} onAction={onRename}>
                  Rename
                </MenuItem>
              )}
              {onDelete && (
                <MenuItem
                  color="danger"
                  iconStart={<RiDeleteBinLine />}
                  onAction={onDelete}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          </MenuTrigger>
        </ArtifactListItem.Action>
      )}
    </ArtifactListItem.Root>
  );
};
